/**
 * Tier Form Component
 * Feature: 024-sla-tier-configuration
 *
 * Form for creating and editing SLA tiers with validation.
 */

import React, { useState, useEffect } from 'react';
import { SLATier, CreateSLATierRequest } from '../../../../shared/types/sla';
import * as slaService from '../../services/slaService';
import { Modal, Button } from '../ui';

interface TierFormProps {
  tier: SLATier | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const TierForm: React.FC<TierFormProps> = ({ tier, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateSLATierRequest>({
    tier_name: '',
    uptime_percent: 99.9,
    rto_hours: 4.0,
    rpo_hours: 1.0,
    penalty_per_hour_usd: 500.0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (tier) {
      setFormData({
        tier_name: tier.tier_name,
        uptime_percent: tier.uptime_percent,
        rto_hours: tier.rto_hours,
        rpo_hours: tier.rpo_hours,
        penalty_per_hour_usd: tier.penalty_per_hour_usd,
      });
    }
  }, [tier]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.tier_name.trim()) {
      newErrors.tier_name = 'Tier name is required';
    }

    if (formData.uptime_percent < 90 || formData.uptime_percent > 99.99) {
      newErrors.uptime_percent = 'Uptime must be between 90.00% and 99.99%';
    }

    if (formData.rto_hours <= 0 || formData.rto_hours > 168) {
      newErrors.rto_hours = 'RTO must be greater than 0 and less than 168 hours (1 week)';
    }

    if (formData.rpo_hours <= 0 || formData.rpo_hours > 24) {
      newErrors.rpo_hours = 'RPO must be greater than 0 and less than 24 hours (1 day)';
    }

    if (formData.penalty_per_hour_usd < 0) {
      newErrors.penalty_per_hour_usd = 'Penalty must be non-negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      if (tier) {
        await slaService.updateTier(tier.id, formData);
      } else {
        await slaService.createTier(formData);
      }
      onSuccess();
    } catch (error) {
      setErrors({
        submit: error instanceof Error ? error.message : 'Failed to save tier',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={tier ? 'Edit SLA Tier' : 'Create SLA Tier'}
      size='md'
      footer={
        <>
          <Button type='button' variant='outline' onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type='submit' form='tier-form' disabled={submitting}>
            {submitting ? 'Saving...' : tier ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form id='tier-form' onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>Tier Name *</label>
          <input
            type='text'
            value={formData.tier_name}
            onChange={(e) => setFormData({ ...formData, tier_name: e.target.value })}
            className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]'
            placeholder='e.g., Enterprise, Standard, Basic'
          />
          {errors.tier_name && <p className='text-red-600 dark:text-red-400 text-sm mt-1'>{errors.tier_name}</p>}
        </div>

        <div>
          <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
            Uptime Percentage * (90.00 - 99.99)
          </label>
          <input
            type='number'
            step='0.01'
            min='90'
            max='99.99'
            value={formData.uptime_percent}
            onChange={(e) =>
              setFormData({ ...formData, uptime_percent: parseFloat(e.target.value) })
            }
            className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]'
          />
          {errors.uptime_percent && (
            <p className='text-red-600 dark:text-red-400 text-sm mt-1'>{errors.uptime_percent}</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
            RTO (Recovery Time Objective) Hours * (0 - 168)
          </label>
          <input
            type='number'
            step='0.25'
            min='0.01'
            max='168'
            value={formData.rto_hours}
            onChange={(e) => setFormData({ ...formData, rto_hours: parseFloat(e.target.value) })}
            className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]'
          />
          {errors.rto_hours && <p className='text-red-600 dark:text-red-400 text-sm mt-1'>{errors.rto_hours}</p>}
        </div>

        <div>
          <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
            RPO (Recovery Point Objective) Hours * (0 - 24)
          </label>
          <input
            type='number'
            step='0.25'
            min='0.01'
            max='24'
            value={formData.rpo_hours}
            onChange={(e) => setFormData({ ...formData, rpo_hours: parseFloat(e.target.value) })}
            className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]'
          />
          {errors.rpo_hours && <p className='text-red-600 dark:text-red-400 text-sm mt-1'>{errors.rpo_hours}</p>}
        </div>

        <div>
          <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
            Penalty per Hour (USD) *
          </label>
          <input
            type='number'
            step='0.01'
            min='0'
            value={formData.penalty_per_hour_usd}
            onChange={(e) =>
              setFormData({ ...formData, penalty_per_hour_usd: parseFloat(e.target.value) })
            }
            className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]'
          />
          {errors.penalty_per_hour_usd && (
            <p className='text-red-600 dark:text-red-400 text-sm mt-1'>{errors.penalty_per_hour_usd}</p>
          )}
        </div>

        {errors.submit && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded'>
            {errors.submit}
          </div>
        )}
      </form>
    </Modal>
  );
};

export default TierForm;
